import React, { useEffect, useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, Typography } from 'src/components/common';
import { InputField } from 'src/components/common/formFields';
import { CompanyAPIService } from 'src/services/company.service';
import { openPopup } from 'src/store/common/common.slice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TGetCompany, TCreateCompanyRequest, TEditCompanyRequest, TGetCompanyPresignedUrlActionRequest } from 'src/services/apiEndpoint.types.ts';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { createCompany, editCompany, getCompanyPresignedUrl, getCompany } from 'src/store/company/company.slice';
import { StorageUtils } from 'src/utils';
import RocketIcon from 'src/assets/images/banner/RocketIcon.svg';
import { TrashIcon, UploadIcon } from 'src/components/common/common.icons';
import { useUploadMedia } from 'src/hooks';
import styles from './CompanySetupModal.module.scss';

interface CompanySetupModalProps {
  show: boolean;
  onHide: () => void;
  authenticatedId: string;
  authenticatedEmail: string;
  existingCompany: TGetCompany | null;
}

const CompanySetupModal: React.FC<CompanySetupModalProps> = ({
  show,
  onHide,
  authenticatedId,
  authenticatedEmail,
  existingCompany
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const companyServiceRef = React.useRef<CompanyAPIService | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyLogoData, setCompanyLogoData] = useState<string | ArrayBuffer | null>(null);
  const uploadImageRef = useRef<HTMLInputElement>(null);
  const selectedImageRef = useRef<HTMLImageElement>(null);
  const dragDropRef = useRef<HTMLDivElement>(null);
  const { handleUploadMedia } = useUploadMedia();
  const { companyProfilePic } = useAppSelector((store) => store.company);
  
  const [formData, setFormData] = useState<{
    startupName: string;
    whatsappNumber: string;
    companyEmailAddress: string;
    termsAccepted: boolean;
  }>({
    startupName: existingCompany?.name || '',
    whatsappNumber: existingCompany?.contactNumber?.toString() || '',
    companyEmailAddress: existingCompany?.companyEmailAddress || authenticatedEmail || '',
    termsAccepted: existingCompany?.termsAndConditions || false
  });

  // Initialize companyService once
  useEffect(() => {
    companyServiceRef.current = new CompanyAPIService();
  }, []);

  // Update form data when company data changes
  useEffect(() => {
    if (existingCompany) {
      setFormData({
        startupName: existingCompany.name || '',
        whatsappNumber: existingCompany.contactNumber?.toString() || '',
        companyEmailAddress: existingCompany.companyEmailAddress || authenticatedEmail || '',
        termsAccepted: existingCompany.termsAndConditions || false
      });
    } else {
      setFormData(prev => ({
        ...prev,
        companyEmailAddress: authenticatedEmail || ''
      }));
    }
  }, [existingCompany, authenticatedEmail]);

  // Initialize company logo if it exists
  useEffect(() => {
    if (existingCompany?.id && companyProfilePic) {
      // Since companyProfilePic is already a URL, we can use it directly
      setCompanyLogoData(companyProfilePic);
    }
  }, [existingCompany?.id, companyProfilePic]);

  const handleInputChange = (field: keyof typeof formData) => (value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openImageFileManager = async () => {
    if (uploadImageRef.current) {
      uploadImageRef.current.click();
    }
  };

  const onMediaLoad = (data: string | ArrayBuffer | null) => {
    setCompanyLogoData(data);
  };

  const removeCompanyLogo = () => {
    setCompanyLogoData(null);
  };

  const renderSelectedImage = () => {
    const ele = selectedImageRef.current;
    if (!ele || !companyLogoData) return;

    if (typeof companyLogoData === 'string') {
      // If it's a URL string, use it directly
      ele.src = companyLogoData;
    } else {
      // If it's ArrayBuffer data, create a blob
      const blob = new Blob([companyLogoData], { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(blob);
      ele.src = imageUrl;
    }
  };

  useEffect(() => {
    renderSelectedImage();
  }, [companyLogoData]);

  const appDispatch = useAppDispatch();
  
  const handleSubmit = async () => {
    if (!formData.startupName || !formData.whatsappNumber || !formData.companyEmailAddress || !formData.termsAccepted) {
      dispatch(
        openPopup({
          popupMessage: 'Please fill all required fields and accept terms & conditions',
          popupType: 'error'
        })
      );
      return;
    }

    // Phone number validation
    if (!/^\d{10}$/.test(formData.whatsappNumber)) {
      dispatch(
        openPopup({
          popupMessage: 'Please enter a valid 10-digit phone number',
          popupType: 'error'
        })
      );
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.companyEmailAddress)) {
      dispatch(
        openPopup({
          popupMessage: 'Please enter a valid email address',
          popupType: 'error'
        })
      );
      return;
    }

    try {
      setLoading(true);

      let companyId: string;

      if (existingCompany?.id) {
        const editCompanyData: TEditCompanyRequest = {
          id: existingCompany.id,
          name: formData.startupName || existingCompany.name,
          type: 'Start up',
          contactNumber: parseInt(formData.whatsappNumber.replace(/\D/g, ''), 10),
          companyEmailAddress: formData.companyEmailAddress,
          termsAndConditions: formData.termsAccepted,
          about: existingCompany.about || '',
          website: existingCompany.website || '',
          introduction: existingCompany.introduction || '',
          foundedYear: existingCompany.foundedYear || 0,
          contactUsUrl: existingCompany.contactUsUrl || '',
          addressLine1: existingCompany.addressLine1 || '',
          addressLine2: existingCompany.addressLine2 || '',
          country: existingCompany.country || '',
          state: existingCompany.state || '',
          city: existingCompany.city || '',
          zipCode: existingCompany.zipCode || '',
          industry: existingCompany.industry || '',
          linkedin: existingCompany.linkedin || '',
          facebook: existingCompany.facebook || '',
          twitter: existingCompany.twitter || '',
          instagram: existingCompany.instagram || ''
        };
        await appDispatch(editCompany(editCompanyData));
        companyId = existingCompany.id;
      } else {
        const createCompanyData: TCreateCompanyRequest = {
          email: authenticatedEmail,
          name: formData.startupName,
          contactNumber: parseInt(formData.whatsappNumber.replace(/\D/g, ''), 10),
          type: 'Start up',
          companyEmailAddress: formData.companyEmailAddress,
          termsAndConditions: formData.termsAccepted,
          consentTimeStamp: new Date().toISOString(),
          navigateTo: '/score'
        };
        
        // Try to create company
        await appDispatch(createCompany(createCompanyData));
        
        // Check if company was created successfully
        const newCompanyId = StorageUtils.get('companyId');
        if (!newCompanyId) {
          // If no companyId was set, creation failed (probably due to 409)
          setLoading(false);
          return;
        }
        companyId = newCompanyId as string;
      }

      // Upload logo if provided
      if (companyLogoData && companyId) {
        try {
          const uploadProfilePicturePayload: TGetCompanyPresignedUrlActionRequest = {
            id: companyId,
            data: companyLogoData,
            showPopup: false
          };
          await appDispatch(getCompanyPresignedUrl(uploadProfilePicturePayload));
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
          dispatch(
            openPopup({
              popupMessage: 'Company details saved but there was an error uploading the logo.',
              popupType: 'warning'
            })
          );
        }
      }

      // Only proceed if we have a valid companyId (no 409 error)
      if (companyId) {
        // Refresh company data to ensure we have the latest state
        await appDispatch(getCompany({ id: companyId }));
        onHide();
        // Navigation is now handled by the createCompany action's success case
      }
    } catch (error) {
      console.error('Error saving company details:', error);
      dispatch(
        openPopup({
          popupMessage: 'An error occurred while saving details',
          popupType: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={styles.setupModal}
    >
      <Modal.Body className={styles.setupModalBody}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.rocketIcon}>
              <img src={RocketIcon} alt="Rocket" />
            </div>
            <h2 className={styles.modalTitle}>Setup Your Company Profile</h2>
          </div>
          <div className={styles.formContent}>
            <div className={styles.formField}>
              <InputField
                id="startup-name"
                label="Company Name"
                value={formData.startupName}
                onChange={(e) => handleInputChange('startupName')(e.target.value)}
                placeholder="xyz.pvt.ltd"
                required
              />
            </div>
            <div className={styles.formField}>
              <InputField
                id="whatsapp-number"
                label="Whatsapp Number"
                value={formData.whatsappNumber}
                onChange={(e) => {
                  // Only allow digits and restrict length to 10
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleInputChange('whatsappNumber')(value);
                }}
                onKeyPress={(e) => {
                  // Prevent non-digit characters
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter your phone number"
                type="tel"
                maxLength={10}
                pattern="[0-9]{10}"
                required
              />
            </div>
            <div className={styles.formField}>
              <InputField
                id="company-email"
                label="Company Email"
                value={formData.companyEmailAddress}
                onChange={(e) => handleInputChange('companyEmailAddress')(e.target.value)}
                placeholder="abc@htraction.com"
                type="email"
                required
              />
            </div>
            <div className={styles.uploadSection}>
  <Typography as="p" variant="caption" fontWeight="bold">
    Upload your company logo
  </Typography>
  <div
    className={styles.uploadLogoContainer}
    draggable
    onClick={openImageFileManager}
    ref={dragDropRef}
    onDrop={(e) => {
      e.preventDefault();
      handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'image', dragDrop: true });
    }}
    onDragOver={(e) => {
      e.preventDefault();
    }}
  >
    <span className={styles.dragDropArea}>
      <UploadIcon />
      <Typography as="p" variant="body-2" fontWeight="regular">
        Click to upload or drag and drop
      </Typography>
      <input
        type="file"
        hidden
        ref={uploadImageRef}
        accept=".bmp,.jpg,.jpeg,.png"
        className={styles.selectPostImage}
        onChange={(e) => {
          handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'image' });
        }}
      />
    </span>
  </div>
  {companyLogoData && (
    <div className={styles.selectedImage}>
      <div
        className={styles.deleteButton}
        onClick={removeCompanyLogo}
      >
        <TrashIcon />
      </div>
      <img alt="uploaded logo" ref={selectedImageRef} />
    </div>
  )}
</div>
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="terms"
                checked={formData.termsAccepted}
                onChange={(e) => handleInputChange('termsAccepted')(e.target.checked)}
              />
              <label htmlFor="terms" className={styles.termsText}>
                I accept the <a href='/termsandconditions' className={styles.termsLink}>terms & conditions</a>
              </label>
            </div>
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              disabled={loading}
              className={styles.submitButton}
              style={{ position: 'relative' }}
            >
              <span style={{ opacity: loading ? 0 : 1 }}>
                Save & Continue
              </span>
              {loading && (
                <div 
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
              )}
            </Button>
            <style>
              {`
                @keyframes spin {
                  0% { transform: translate(-50%, -50%) rotate(0deg); }
                  100% { transform: translate(-50%, -50%) rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CompanySetupModal;
